import { distanceUnitByUnitType, intoMeters } from '@racemap/utilities/functions/units';
import { calculateDistanceInMeter } from '@racemap/utilities/point_utils';
import type { DistanceUnit, UnitType } from '@racemap/utilities/types/units';
import type { FeatureCollection, Point } from 'geojson';

/** A polyline addressed by index, so a `TrackView` over a `Float64Array` needs no copying. */
export type IndexedLine = {
  length: number;
  at: (index: number) => [number, number];
};

export type ZoomBand = { stride: number; minzoom: number; maxzoom: number };

/**
 * One stride per zoom range, non-overlapping, so exactly one is drawn at a time. Below zoom 5 no
 * markers are drawn at all.
 */
const ZOOM_BANDS: Array<ZoomBand> = [
  { stride: 1, minzoom: 12.7, maxzoom: 24 },
  { stride: 2, minzoom: 12, maxzoom: 12.7 },
  { stride: 5, minzoom: 10.7, maxzoom: 12 },
  { stride: 10, minzoom: 10, maxzoom: 10.7 },
  { stride: 20, minzoom: 8.5, maxzoom: 10 },
  { stride: 50, minzoom: 7.5, maxzoom: 8.5 },
  { stride: 100, minzoom: 7, maxzoom: 7.5 },
  { stride: 200, minzoom: 6, maxzoom: 7 },
  { stride: 500, minzoom: 5, maxzoom: 6 },
];

/** The bands worth drawing for a line that carries `markerCount` markers. */
export function visibleZoomBands(markerCount: number): Array<ZoomBand> {
  return ZOOM_BANDS.filter((band) => band.stride <= markerCount);
}

export type DistanceMarkerProperties = {
  distance: number;
  unit: string;
};

/**
 * Markers at every full distance unit along `line`. `metersPerIndex`, the uniform spacing of a
 * chunked shadowtrack, turns placement into index arithmetic and skips all measuring.
 */
export function buildDistanceMarkerFeatures(
  line: IndexedLine,
  unitType: UnitType,
  metersPerIndex?: number,
): FeatureCollection<Point, DistanceMarkerProperties> {
  const unitLabel = distanceUnitByUnitType(unitType) as DistanceUnit;
  const metersPerUnit = intoMeters(1, unitLabel);
  const positions =
    metersPerIndex != null
      ? uniformStepPositions(line, metersPerUnit / metersPerIndex)
      : measuredPositions(line, metersPerUnit);

  return {
    type: 'FeatureCollection',
    features: positions.map((position, i) => ({
      type: 'Feature' as const,
      properties: { distance: i + 1, unit: unitLabel },
      geometry: { type: 'Point' as const, coordinates: position },
    })),
  };
}

/** Vertices are a known distance apart, so a marker is one lookup and nothing is measured. */
function uniformStepPositions(line: IndexedLine, indicesPerUnit: number): Array<[number, number]> {
  const positions: Array<[number, number]> = [];
  for (let unit = 1; unit * indicesPerUnit < line.length; unit++) {
    positions.push(line.at(Math.floor(unit * indicesPerUnit)));
  }
  return positions;
}

/**
 * Interpolates at every exact unit boundary, so a marker lands on its kilometre however sparsely
 * the line was drawn. `chunkLineString`'s maths, run at the unit length rather than at 10 m.
 */
function measuredPositions(line: IndexedLine, metersPerUnit: number): Array<[number, number]> {
  const positions: Array<[number, number]> = [];
  let covered = 0;

  for (let index = 1; index < line.length; index++) {
    const [fromLng, fromLat] = line.at(index - 1);
    const [toLng, toLat] = line.at(index);
    const segment = calculateDistanceInMeter(fromLat, fromLng, toLat, toLng);
    // The short way round, so a segment across the antimeridian interpolates over its 2° gap
    // rather than the 358° the raw difference reads. Mapbox renders a longitude past ±180 wrapped.
    const deltaLng = ((((toLng - fromLng + 180) % 360) + 360) % 360) - 180;

    // Multiplied out rather than accumulated, so a long line does not drift.
    let boundary = (positions.length + 1) * metersPerUnit;
    while (boundary <= covered + segment && segment > 0) {
      const scale = (boundary - covered) / segment;
      positions.push([fromLng + scale * deltaLng, fromLat + scale * (toLat - fromLat)]);
      boundary += metersPerUnit;
    }
    covered += segment;
  }
  return positions;
}
